<template>
  <div class="inspect-manage">
    <div class="page-header">
      <h2>隐患排查管理</h2>
      <p>管理隐患排查项目，跟踪排查进度和结果</p>
    </div>

    <el-card class="stats-card">
      <div class="stats-content">
        <div class="stat-item">
          <div class="stat-label">总排查项目</div>
          <div class="stat-value">{{ totalInspectCount }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-label">已完成</div>
          <div class="stat-value completed">{{ completedInspectCount }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-label">未完成</div>
          <div class="stat-value pending">{{ pendingInspectCount }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-label">完成率</div>
          <div class="stat-value rate">{{ completionRate }}%</div>
        </div>
      </div>
    </el-card>

    <el-card class="filter-card">
      <div class="filter-content">
        <div class="filter-left">
          <el-select v-model="filter.status" placeholder="状态筛选" clearable>
            <el-option label="全部" :value="null" />
            <el-option label="已完成" :value="true" />
            <el-option label="未完成" :value="false" />
          </el-select>
        </div>
        <div class="filter-actions">
          <el-button type="primary" icon="el-icon-plus" @click="handleAdd">
            新增排查项
          </el-button>
          <el-button type="info" icon="el-icon-refresh" @click="refresh">
            刷新
          </el-button>
        </div>
      </div>
    </el-card>

    <el-card>
      <el-table :data="filteredItems" border style="width: 100%" v-loading="loading">
        <el-table-column prop="id" label="ID" width="80" align="center"></el-table-column>
        <el-table-column prop="title" label="排查项目" min-width="200"></el-table-column>
        <el-table-column prop="description" label="排查内容" min-width="250"></el-table-column>
        <el-table-column label="指定用户" width="120" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.assignedUserName" type="info" size="small">
              {{ scope.row.assignedUserName }}
            </el-tag>
            <span v-else class="no-assign">未指定</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag
              :type="scope.row.completed ? 'success' : 'warning'"
              size="medium"
            >
              {{ scope.row.completed ? '已完成' : '未完成' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="排查照片" width="120" align="center">
          <template slot-scope="scope">
            <template v-if="scope.row.photos && scope.row.photos.length > 0">
              <el-badge :value="scope.row.photos.length" class="photo-badge">
                <el-image
                  style="width: 60px; height: 60px; cursor: pointer;"
                  :src="getFullUrl(scope.row.photos[0].photo_url)"
                  fit="cover"
                  @click="showPhotos(scope.row.photos)"
                ></el-image>
              </el-badge>
            </template>
            <span v-else class="no-photo">暂无</span>
          </template>
        </el-table-column>
        <el-table-column prop="completedTime" label="完成时间" width="150">
          <template slot-scope="scope">
            {{ scope.row.completedTime || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button
              type="primary"
              icon="el-icon-view"
              size="mini"
              @click="handleView(scope.row)"
            >查看</el-button>
            <el-button
              type="warning"
              icon="el-icon-edit"
              size="mini"
              @click="handleEdit(scope.row)"
            >编辑</el-button>
            <el-button
              type="danger"
              icon="el-icon-delete"
              size="mini"
              @click="handleDelete(scope.row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="500px"
      @close="resetForm"
    >
      <el-form
        ref="inspectForm"
        :model="formData"
        :rules="formRules"
        label-width="80px"
      >
        <el-form-item label="项目标题" prop="title">
          <el-input
            v-model="formData.title"
            placeholder="请输入排查项目标题"
            maxlength="50"
            show-word-limit
          />
        </el-form-item>
        
        <el-form-item label="排查内容" prop="description">
          <el-input
            v-model="formData.description"
            type="textarea"
            :rows="4"
            placeholder="请输入排查内容和要求"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>
        
        <el-form-item label="指定用户" prop="assignedUserId">
          <el-select
            v-model="formData.assignedUserId"
            placeholder="请选择负责排查的用户"
            clearable
            style="width: 100%"
          >
            <el-option
              v-for="user in users"
              :key="user.id"
              :label="user.name"
              :value="user.id"
            />
          </el-select>
          <div class="form-tip">选择指定用户负责此项排查任务，用户可在小程序上传排查照片（最多20张）</div>
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">
          确认
        </el-button>
      </div>
    </el-dialog>

    <!-- 查看对话框 -->
    <el-dialog
      title="查看排查项"
      :visible.sync="viewDialogVisible"
      width="600px"
    >
      <div v-if="currentItem" class="view-content">
        <div class="view-item">
          <label class="view-label">项目标题：</label>
          <span class="view-value">{{ currentItem.title }}</span>
        </div>
        <div class="view-item">
          <label class="view-label">排查内容：</label>
          <span class="view-value">{{ currentItem.description }}</span>
        </div>
        <div class="view-item">
          <label class="view-label">指定用户：</label>
          <span class="view-value">{{ currentItem.assignedUserName || '未指定' }}</span>
        </div>
        <div class="view-item">
          <label class="view-label">状态：</label>
          <el-tag
            :type="currentItem.completed ? 'success' : 'warning'"
            size="medium"
          >
            {{ currentItem.completed ? '已完成' : '未完成' }}
          </el-tag>
        </div>
        <div v-if="currentItem.completedTime" class="view-item">
          <label class="view-label">完成时间：</label>
          <span class="view-value">{{ currentItem.completedTime }}</span>
        </div>
        <div v-if="currentItem.photos && currentItem.photos.length > 0" class="view-item">
          <label class="view-label">排查照片：</label>
          <div class="photos-grid">
            <el-image
              v-for="(photo, index) in currentItem.photos"
              :key="photo.id"
              style="width: 100px; height: 100px; margin: 5px;"
              :src="getFullUrl(photo.photo_url)"
              fit="cover"
              :preview-src-list="currentItem.photos.map(p => getFullUrl(p.photo_url))"
              :initial-index="index"
            ></el-image>
          </div>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="viewDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 照片预览对话框 -->
    <el-dialog
      title="排查照片"
      :visible.sync="photoDialogVisible"
      width="800px"
    >
      <div class="photos-gallery">
        <el-image
          v-for="(photo, index) in currentPhotos"
          :key="photo.id"
          style="width: 150px; height: 150px; margin: 8px;"
          :src="getFullUrl(photo.photo_url)"
          fit="cover"
          :preview-src-list="currentPhotos.map(p => getFullUrl(p.photo_url))"
          :initial-index="index"
        ></el-image>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import config from '@/config'

export default {
  name: 'InspectManagement',
  data() {
    return {
      loading: false,
      dialogVisible: false,
      viewDialogVisible: false,
      photoDialogVisible: false,
      submitting: false,
      isEdit: false,
      currentId: null,
      currentItem: null,
      currentPhotos: [],
      staticUrl: config.staticUrl,
      filter: {
        status: null
      },
      formData: {
        title: '',
        description: '',
        assignedUserId: null
      },
      formRules: {
        title: [
          { required: true, message: '请输入项目标题', trigger: 'blur' }
        ],
        description: [
          { required: true, message: '请输入排查内容', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    ...mapState(['inspectItems', 'users']),
    ...mapGetters(['completedInspectCount', 'totalInspectCount']),
    filteredItems() {
      if (this.filter.status === null) {
        return this.inspectItems
      }
      return this.inspectItems.filter(item => item.completed === this.filter.status)
    },
    pendingInspectCount() {
      return this.totalInspectCount - this.completedInspectCount
    },
    completionRate() {
      return this.totalInspectCount > 0 
        ? Math.round((this.completedInspectCount / this.totalInspectCount) * 100) 
        : 0
    },
    dialogTitle() {
      return this.isEdit ? '编辑排查项' : '新增排查项'
    }
  },
  async created() {
    await this.loadData()
  },
  methods: {
    ...mapActions(['updateInspectItem', 'addInspectItem', 'deleteInspectItem', 'fetchInspectItems', 'fetchUsers']),
    getFullUrl(url) {
      if (!url) return ''
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url
      }
      return this.staticUrl + url
    },
    async loadData() {
      this.loading = true
      try {
        await Promise.all([
          this.fetchInspectItems(),
          this.fetchUsers()
        ])
      } catch (error) {
        this.$message.error('加载数据失败：' + error.message)
      } finally {
        this.loading = false
      }
    },
    async refresh() {
      await this.loadData()
      this.$message.success('刷新成功')
    },
    handleAdd() {
      this.isEdit = false
      this.dialogVisible = true
    },
    handleEdit(row) {
      this.isEdit = true
      this.currentId = row.id
      this.formData = {
        title: row.title,
        description: row.description,
        assignedUserId: row.assignedUserId
      }
      this.dialogVisible = true
    },
    handleView(row) {
      this.currentItem = row
      this.viewDialogVisible = true
    },
    showPhotos(photos) {
      this.currentPhotos = photos
      this.photoDialogVisible = true
    },
    handleDelete(row) {
      this.$confirm('确定要删除该排查项吗？删除后将无法恢复。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await this.deleteInspectItem(row.id)
          this.$message.success('删除成功')
        } catch (error) {
          this.$message.error('删除失败：' + error.message)
        }
      }).catch(() => {
        this.$message.info('已取消删除')
      })
    },
    submitForm() {
      this.$refs.inspectForm.validate(async valid => {
        if (valid) {
          this.submitting = true
          
          try {
            const submitData = {
              title: this.formData.title,
              description: this.formData.description,
              assigned_user_id: this.formData.assignedUserId
            }
            
            if (this.isEdit) {
              await this.updateInspectItem({
                id: this.currentId,
                item: submitData
              })
              this.$message.success('更新成功')
            } else {
              await this.addInspectItem(submitData)
              this.$message.success('新增成功')
            }
            
            this.dialogVisible = false
            this.resetForm()
          } finally {
            this.submitting = false
          }
        }
      })
    },
    resetForm() {
      if (this.$refs.inspectForm) {
        this.$refs.inspectForm.resetFields()
      }
      this.isEdit = false
      this.currentId = null
      this.formData = {
        title: '',
        description: '',
        assignedUserId: null
      }
    }
  }
}
</script>

<style scoped>
.inspect-manage {
  padding: 20px;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0 0 8px 0;
  font-size: 24px;
  color: #333;
}

.page-header p {
  margin: 0;
  color: #999;
  font-size: 14px;
}

.stats-card {
  margin-bottom: 20px;
}

.stats-content {
  display: flex;
  justify-content: space-around;
  text-align: center;
}

.stat-item {
  padding: 20px;
}

.stat-label {
  color: #666;
  font-size: 14px;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 32px;
  font-weight: bold;
  color: #333;
}

.stat-value.completed {
  color: #67c23a;
}

.stat-value.pending {
  color: #e6a23c;
}

.stat-value.rate {
  color: #409eff;
}

.filter-card {
  margin-bottom: 20px;
}

.filter-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-left {
  display: flex;
  gap: 10px;
}

.filter-actions {
  display: flex;
  gap: 10px;
}

.no-photo {
  color: #999;
  font-size: 13px;
}

.photo-badge {
  cursor: pointer;
}

.form-tip {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.no-assign {
  color: #999;
  font-size: 13px;
}

.view-content {
  padding: 0 10px;
}

.view-item {
  display: flex;
  margin-bottom: 16px;
  align-items: flex-start;
}

.view-label {
  min-width: 80px;
  color: #666;
  font-weight: 500;
  margin-right: 10px;
  line-height: 1.5;
}

.view-value {
  flex: 1;
  color: #333;
  line-height: 1.5;
}

.photos-grid {
  display: flex;
  flex-wrap: wrap;
}

.photos-gallery {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}
</style>
